import { isPlatformBrowser } from '@angular/common';
import { HttpClient, HttpContext } from '@angular/common/http';
import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { environment } from 'environments/environment';
import { ABC_AUTH_API } from './core/auth-abc/auth-abc-api.interceptor';
import { AppConstant } from './core/constant/app-constant';
import { DevicePlatform, RoleCode } from './core/enums';
import { DeviceDetails, DeviceScreenResolution } from './core/interfaces';
import { LocalStorageService } from './core/services/local-storage.service';
import { UserCountryService } from './core/services/user-country.service';

const httpOptions = {
    context: new HttpContext().set(ABC_AUTH_API, true),
};

@Injectable({
    providedIn: 'root',
})
export class AppService {
    constructor(
        private readonly http: HttpClient,
        private readonly localStorageService: LocalStorageService,
        private readonly userCountryService: UserCountryService,
        @Inject(PLATFORM_ID) private readonly platformId: object
    ) {}

    createUniqueDeviceId(): string {
        if (
            globalThis.crypto !== undefined &&
            typeof globalThis.crypto.randomUUID === 'function'
        ) {
            return globalThis.crypto.randomUUID();
        }

        return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
    }

    checkLocalStorageForDeviceId(): string {
        const deviceIdKey = AppConstant.Keys.deviceId;
        let deviceId = this.localStorageService.get(deviceIdKey);

        if (!deviceId || deviceId === 'undefined') {
            this.localStorageService.set(
                deviceIdKey,
                this.createUniqueDeviceId()
            );
            deviceId = this.localStorageService.get(deviceIdKey);
        }

        return deviceId ?? '';
    }

    getDeviceDetails(role: RoleCode = RoleCode.VIEWER): DeviceDetails {
        const deviceId = this.checkLocalStorageForDeviceId();
        const baseDetails: DeviceDetails = {
            deviceId,
            platform: DevicePlatform.WEB,
            role,
            deviceModel: null,
            manufacturer: null,
            osVersion: null,
            appVersion: null,
            language: null,
            timezone: null,
            screenResolution: null,
            fcmToken: null,
            city: null,
            state: null,
            country: null,
        };

        if (!isPlatformBrowser(this.platformId)) {
            return baseDetails;
        }

        const navigatorRef = globalThis.navigator;
        if (!navigatorRef) {
            return baseDetails;
        }

        const language = this.sanitizeValue(navigatorRef.language, 10);
        const timezone = this.sanitizeValue(this.getTimeZone(), 100);
        const appVersion = environment.version;
        const deviceModel = this.extractDeviceModel(navigatorRef);
        const manufacturer = this.extractManufacturer(navigatorRef);
        const osVersion = this.sanitizeValue(navigatorRef.userAgent, 50);
        const fcmToken = this.getStoredFcmToken();
        const screenResolution = this.getScreenResolution();
        const country = this.getCountryCode();

        return {
            ...baseDetails,
            deviceModel,
            manufacturer,
            osVersion,
            appVersion,
            language,
            timezone,
            screenResolution,
            fcmToken,
            country,
        };
    }

    sendDeviceDetails(role: RoleCode = RoleCode.VIEWER) {
        const payload = this.getDeviceDetails(role);
        return this.http.post(`/user/devices`, payload, httpOptions);
    }

    private getScreenResolution(): DeviceScreenResolution | null {
        if (!isPlatformBrowser(this.platformId)) {
            return null;
        }

        const screenRef = globalThis.screen;
        if (!screenRef) {
            return null;
        }

        const width = Math.max(0, Math.trunc(screenRef.width ?? 0));
        const height = Math.max(0, Math.trunc(screenRef.height ?? 0));

        return {
            width,
            height,
        };
    }

    private getStoredFcmToken(): string | null {
        const storedToken = this.localStorageService.get(
            AppConstant.Keys.fcmToken
        );
        if (typeof storedToken !== 'string') {
            return null;
        }

        const trimmedToken = storedToken.trim();
        return trimmedToken || null;
    }

    private getTimeZone(): string | null {
        try {
            return Intl.DateTimeFormat().resolvedOptions().timeZone ?? null;
        } catch (error) {
            console.warn('Unable to determine timezone:', error);
            return null;
        }
    }

    private extractDeviceModel(navigatorRef: Navigator): string | null {
        const userAgentData = (
            navigatorRef as Navigator & {
                userAgentData?: { platform?: string; model?: string };
            }
        ).userAgentData;
        const platformInfo = userAgentData?.platform ?? null;
        const modelInfo = userAgentData?.model ?? null;
        const combinedInfo = [platformInfo, modelInfo]
            .filter((value) => !!value)
            .join(' ')
            .trim();

        if (combinedInfo) {
            return combinedInfo.slice(0, 100);
        }

        const userAgent = navigatorRef.userAgent ?? null;
        if (!userAgent) {
            return null;
        }

        return userAgent.slice(0, 100);
    }

    private extractManufacturer(navigatorRef: Navigator): string | null {
        const userAgentData = (
            navigatorRef as Navigator & {
                userAgentData?: { brands?: { brand: string }[] };
            }
        ).userAgentData;

        const brandEntry = userAgentData?.brands?.find(
            (entry) => entry.brand && entry.brand !== 'Not A(Brand)'
        );

        return this.sanitizeValue(brandEntry?.brand ?? null, 100);
    }

    private getCountryCode(): string | null {
        if (!this.userCountryService?._countryCode) {
            return null;
        }

        try {
            const code = this.userCountryService._countryCode();
            return this.sanitizeValue(code, 100);
        } catch (error) {
            console.warn(
                'Unable to fetch country code from UserCountryService:',
                error
            );
            return null;
        }
    }

    private sanitizeValue(
        value: string | null | undefined,
        maxLength: number
    ): string | null {
        if (!value) {
            return null;
        }

        const trimmedValue = value.trim();
        if (!trimmedValue) {
            return null;
        }

        return trimmedValue.slice(0, maxLength);
    }
}
